import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  Users,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  UserCheck,
  Mail,
  Phone,
  Calendar
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const StudentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { company } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const classes = [
    'Berçário', 'Maternal I', 'Maternal II', 'Jardim I', 'Jardim II',
    '1º Ano', '2º Ano', '3º Ano', '4º Ano', '5º Ano',
    '6º Ano', '7º Ano', '8º Ano', '9º Ano',
    '1º Ensino Médio', '2º Ensino Médio', '3º Ensino Médio'
  ];

  useEffect(() => {
    if (company?.id) {
      loadStudents();
    }
  }, [company]);

  useEffect(() => {
    // Mostrar mensagem de sucesso se veio da página de cadastro
    if (location.state?.message) {
      toast.success(location.state.message);
      // Limpar state para não mostrar novamente
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getStudents(company.id);
      setStudents(data || []);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Erro ao carregar alunos');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteStudent = async (student) => {
    const studentName = student.people?.name || student.name || student.registration_number;
    if (!window.confirm(`Tem certeza que deseja excluir o aluno "${studentName}"?`)) {
      return;
    }

    try {
      await dataHelpers.deleteStudent(student.id);
      setStudents(prev => prev.filter(s => s.id !== student.id));
      toast.success('Aluno excluído com sucesso');
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Erro ao excluir aluno');
    }
  };
  const filteredStudents = students.filter(student => {
    const studentName = (student.people?.name || student.name || '').toLowerCase();
    const registrationNumber = student.registration_number?.toString() || '';
    const parentName = (student.person?.name || '').toLowerCase();
    
    const matchesSearch = studentName.includes(searchTerm.toLowerCase()) ||
                         registrationNumber.includes(searchTerm) ||
                         parentName.includes(searchTerm.toLowerCase());
    
    const matchesClass = !selectedClass || student.class_name === selectedClass;
    
    const matchesStatus = !selectedStatus || 
      (selectedStatus === 'active' && student.active) ||
      (selectedStatus === 'inactive' && !student.active) ||
      (selectedStatus === 'low_balance' && student.balance < 10) ||
      (selectedStatus === 'negative_balance' && student.balance < 0);
    
    return matchesSearch && matchesClass && matchesStatus;
  });

  const stats = {
    total: students.length,
    active: students.filter(s => s.active).length,
    inactive: students.filter(s => !s.active).length,
    lowBalance: students.filter(s => s.balance < 10).length,
    negativeBalance: students.filter(s => s.balance < 0).length,
    totalBalance: students.reduce((sum, s) => sum + (s.balance || 0), 0)
  };

  const getBalanceStatus = (balance) => {
    if (balance < 0) {
      return { status: 'negative', label: 'Saldo negativo', color: 'bg-red-100 text-red-800', icon: XCircle };
    } else if (balance < 10) {
      return { status: 'low', label: 'Saldo baixo', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle };
    } else {
      return { status: 'ok', label: 'Saldo ok', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
  };

  const getPaymentTypeLabel = (type) => {
    const types = {
      prepaid: 'Pré-pago',
      credit: 'Crédito',
      monthly: 'Mensalista'
    };
    return types[type] || type;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alunos</h1>
            <p className="text-gray-600">
              Gerencie os alunos cadastrados na cantina
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/dashboard/students/new">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Aluno
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Alunos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">{stats.active} ativos</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Saldo Baixo</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.lowBalance}</p>
                <p className="text-sm text-gray-500">menos de R$ 10,00</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Saldo Negativo</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.negativeBalance}</p>
                <p className="text-sm text-gray-500">em débito</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Saldo Total</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(stats.totalBalance)}
                </p>
                <p className="text-sm text-gray-500">em créditos</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Buscar alunos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-blue-50 border-blue-300' : ''}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtros
                </Button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Turma
                  </label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todas as turmas</option>
                    {classes.map(className => (
                      <option key={className} value={className}>
                        {className}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos os status</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                    <option value="low_balance">Saldo baixo</option>
                    <option value="negative_balance">Saldo negativo</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </Card>        {/* Students List */}
        <Card>
          <div className="overflow-hidden">            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                    Aluno
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 hidden sm:table-cell">
                    Turma
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 hidden lg:table-cell">
                    Responsável
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Saldo
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 hidden md:table-cell">
                    Status
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 md:px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {searchTerm || selectedClass || selectedStatus
                          ? 'Nenhum aluno encontrado com os filtros aplicados'
                          : 'Nenhum aluno cadastrado ainda'
                        }
                      </p>
                      {!searchTerm && !selectedClass && !selectedStatus && (
                        <Link to="/dashboard/students/new">
                          <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Cadastrar Primeiro Aluno
                          </Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student) => {
                    const balanceStatus = getBalanceStatus(student.balance || 0);
                    const StatusIcon = balanceStatus.icon;
                    
                    return (                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10">
                              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <Users className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-3 md:ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {student.people?.name || student.name || 'Nome não disponível'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                Mat: {student.registration_number}
                              </div>
                              <div className="text-xs text-gray-500 truncate md:hidden">
                                {student.class_name || 'Sem turma'}
                              </div>
                            </div>
                          </div>
                        </td>                        <td className="px-2 md:px-4 py-4 hidden sm:table-cell">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {student.class_name || 'Não definida'}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-900">
                            {student.person?.name || 'Não informado'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {student.person?.email || ''}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(student.balance || 0)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getPaymentTypeLabel(student.payment_type)}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden md:table-cell">
                          <div className="space-y-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${balanceStatus.color}`}>
                              <StatusIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span>{balanceStatus.label}</span>
                            </span>
                            <div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                student.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                <UserCheck className="w-3 h-3 mr-1 flex-shrink-0" />
                                <span>{student.active ? 'Ativo' : 'Inativo'}</span>
                              </span>
                            </div>
                          </div>
                        </td>                        <td className="px-2 py-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button 
                              onClick={() => navigate(`/dashboard/students/${student.id}`)}
                              title="Ver detalhes"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => navigate(`/dashboard/students/${student.id}/edit`)}
                              title="Editar aluno"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors duration-200"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteStudent(student)}
                              title="Excluir aluno"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
    </div>
  );
};

export default StudentsPage;